import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaUtensils, FaWeight, FaCalendarAlt, FaChartLine, FaClipboardList, FaUser, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { doc, getDoc, collection, getDocs, query, where, updateDoc, setDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  z-index: 1;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.1rem;
  opacity: 0.9;
  position: relative;
  z-index: 1;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const StatCard = styled(Card)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
`;

const StatContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #667eea;
  background: linear-gradient(135deg, #667eea20, #764ba220);
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #718096;
  font-size: 0.95rem;
  font-weight: 500;
`;

const SectionContainer = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
  color: #667eea;
`;

// Enhanced Plan Card Components
const PlanCard = styled(Card)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  border: none;
  position: relative;
`;

const PlanHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: white;
    border-radius: 20px 20px 0 0;
  }
`;

const PlanTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PlanTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
`;

const PlanStatus = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${props => {
    if (props.status === 'expired') return 'rgba(248, 113, 113, 0.2)';
    return 'rgba(34, 197, 94, 0.2)';
  }};
  color: ${props => {
    if (props.status === 'expired') return '#dc2626';
    return '#16a34a';
  }};
  border: 2px solid ${props => {
    if (props.status === 'expired') return 'rgba(248, 113, 113, 0.3)';
    return 'rgba(34, 197, 94, 0.3)';
  }};
`;

const PlanContent = styled.div`
  padding: 2rem;
  padding-top: 1rem;
`;

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProgressLabel = styled.span`
  font-weight: 600;
  color: #4a5568;
`;

const ProgressPercentage = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: #667eea;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background-color: #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${props => props.progress}%;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: #718096;
`;

const PlanDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const PlanDetail = styled.div`
  background: #f7fafc;
  padding: 1.25rem;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.8rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-weight: 600;
    color: #2d3748;
    font-size: 1.1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const EnhancedButton = styled(Button)`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  h3 {
    margin-bottom: 0.75rem;
    color: #4a5568;
    font-size: 1.5rem;
  }
  
  p {
    color: #718096;
    margin-bottom: 2rem;
    font-size: 1.1rem;
  }
`;

const ContentCard = styled(Card)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: none;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ContentItem = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8fafc;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
  font-size: 1.1rem;
`;

const ItemDescription = styled.div`
  color: #718096;
  font-size: 0.95rem;
`;

const ViewAllButton = styled.div`
  padding: 1.5rem 2rem;
  text-align: center;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
  font-size: 1.2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const CustomerDashboard = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planProgress, setPlanProgress] = useState(null);
  const [mealPlans, setMealPlans] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    if (date instanceof Date) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    return 'N/A';
  };

  // Helper function to convert date to Date object
  const convertToDate = (date) => {
    if (!date) return null;
    
    if (date.toDate) {
      return date.toDate();
    }
    
    if (typeof date === 'string') {
      return new Date(date);
    }
    
    if (date instanceof Date) {
      return date;
    }
    
    return null;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Fetch user info
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          
          // Fetch health data
          const healthRef = doc(db, 'users', currentUser.uid, 'health', 'profile');
          const healthDoc = await getDoc(healthRef);
          
          if (healthDoc.exists()) {
            setHealthData(healthDoc.data());
          }
          
          // Load selected plan
          try {
            const selectedPlanId = userData.selectedPlanId;
            if (selectedPlanId) {
              const planRef = doc(db, 'plans', selectedPlanId);
              const planDoc = await getDoc(planRef);
              
              if (planDoc.exists()) {
                const planData = planDoc.data();
                const combinedPlan = {
                  ...planData,
                  planId: selectedPlanId,
                  selectedAt: userData.planSelectedAt || null
                };
                setSelectedPlan(combinedPlan);
                
                // Fetch or create plan progress
                const progressRef = doc(db, 'users', currentUser.uid, 'planProgress', 'current');
                const progressDoc = await getDoc(progressRef);
                
                if (progressDoc.exists()) {
                  const progressData = progressDoc.data();
                  setPlanProgress(progressData);
                  
                  // Update progress calculation
                  await updatePlanProgress(combinedPlan, progressData, userData.planSelectedAt);
                } else {
                  // Create initial progress document
                  const planStartDate = convertToDate(userData.planSelectedAt) || new Date();
                  const today = new Date();
                  const totalDays = parseInt(planData.duration) || 30;
                  const daysPassed = Math.floor((today - planStartDate) / (1000 * 60 * 60 * 24));
                  const initialProgressPercentage = Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));
                  
                  const initialProgress = {
                    startDate: planStartDate,
                    progressPercentage: initialProgressPercentage,
                    lastUpdated: today,
                    planId: selectedPlanId
                  };
                  
                  await setDoc(progressRef, initialProgress);
                  setPlanProgress(initialProgress);
                }
              }
            }
          } catch (err) {
            console.error('Error loading selected plan:', err);
          }
          
          // Fetch meal plans
          try {
            const mealPlansRef = collection(db, 'users', currentUser.uid, 'mealPlans');
            const mealPlansDocs = await getDocs(mealPlansRef);
            
            const mealPlansData = [];
            mealPlansDocs.forEach(doc => {
              mealPlansData.push({
                id: doc.id,
                ...doc.data()
              });
            });
            setMealPlans(mealPlansData);
          } catch (err) {
            console.error('Error loading meal plans:', err);
          }

          // Fetch consultations
          try {
            const consultationsRef = collection(db, 'users', currentUser.uid, 'consultations');
            const consultationsQuery = query(consultationsRef, where('status', '==', 'scheduled'));
            const consultationsDocs = await getDocs(consultationsQuery);
            
            const consultationsData = [];
            consultationsDocs.forEach(doc => {
              consultationsData.push({
                id: doc.id,
                ...doc.data()
              });
            });
            setConsultations(consultationsData);
          } catch (err) {
            console.error('Error loading consultations:', err);
          }

          // Fetch partners
          try {
            const partnersRef = collection(db, 'users', currentUser.uid, 'partners');
            const partnersDocs = await getDocs(partnersRef);
            
            const partnersData = [];
            partnersDocs.forEach(doc => {
              partnersData.push({
                id: doc.id,
                ...doc.data()
              });
            });
            setPartners(partnersData);
          } catch (err) {
            console.error('Error loading partners:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser]);

  const updatePlanProgress = async (plan, progress, planSelectedAt) => {
    if (!plan) return;

    const planStartDate = convertToDate(planSelectedAt);
    
    if (!planStartDate) {
      console.error('No valid plan start date found');
      return;
    }

    const today = new Date();
    const totalDays = parseInt(plan.duration) || 30;
    const daysPassed = Math.floor((today - planStartDate) / (1000 * 60 * 60 * 24));
    const newProgress = Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));
    
    const updatedProgress = {
      ...(progress || {}),
      progressPercentage: newProgress,
      lastUpdated: today,
      startDate: planStartDate,
      planId: plan.planId
    };
    
    const progressRef = doc(db, 'users', currentUser.uid, 'planProgress', 'current');
    await updateDoc(progressRef, updatedProgress);
    setPlanProgress(updatedProgress);
  };

  const rebuyPlan = async () => {
  if (!selectedPlan || !currentUser) return;
  
  try {
    const today = new Date();
    
    // Update user document with new plan selection date
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
  planSelectedAt: today,
  planPausedDays: 0,
  isPlanPaused: false,
  planPausedAt: null
});

    
    // Reset plan progress
    const progressRef = doc(db, 'users', currentUser.uid, 'planProgress', 'current');
    const newProgress = {
      startDate: today,
      progressPercentage: 0,
      lastUpdated: today,
      planId: selectedPlan.planId
    };
    await setDoc(progressRef, newProgress);
    
    // Update local state
    setUserData(prev => ({ ...prev, planSelectedAt: today }));
    setPlanProgress(newProgress);
    
    alert('Plan reactivated successfully!');
  } catch (error) {
    console.error('Error rebuying plan:', error);
    alert('Failed to reactivate plan. Please try again.');
  }
};
  const getPlanStatus = () => {
    if (!selectedPlan || !planProgress) return 'unknown';
    
    if (planProgress.progressPercentage >= 100) return 'expired';
    return 'active';
  };

const getDaysRemaining = () => {
  if (!selectedPlan || !planProgress || !userData?.planSelectedAt) return 'N/A';

  const planStartDate = convertToDate(userData.planSelectedAt);
  if (!planStartDate) return 'N/A';

  const totalDays = parseInt(selectedPlan.duration) || 30;
  const pausedDays = userData?.planPausedDays || 0;
  
  // Add paused days to extend the plan duration
  const adjustedTotalDays = totalDays + pausedDays;

  const today = new Date();
  const daysPassed = Math.floor((today - planStartDate) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, adjustedTotalDays - daysPassed);

  return daysRemaining;
};

const getPlanEndDate = () => {
  if (!selectedPlan || !userData?.planSelectedAt) return 'N/A';

  const planStartDate = convertToDate(userData.planSelectedAt);
  if (!planStartDate) return 'N/A';

  const totalDays = parseInt(selectedPlan.duration) || 30;
  const pausedDays = userData?.planPausedDays || 0;
  
  // Calculate end date with extension for paused days
  const endDate = new Date(planStartDate);
  endDate.setDate(planStartDate.getDate() + totalDays + pausedDays);
  
  return formatDate(endDate);
};

  // Calculate BMI if health data exists
  const calculateBMI = () => {
    if (!healthData || !healthData.height || !healthData.weight) return 'N/A';
    
    const heightInMeters = healthData.height / 100;
    const bmi = (healthData.weight / (heightInMeters * heightInMeters)).toFixed(1);
    return bmi;
  };

  // Show loading state
  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading your dashboard...</LoadingMessage>
      </PageContainer>
    );
  }

  const planStatus = getPlanStatus();
  
  return (
    <PageContainer>
      <Header>
        <Title>Welcome Back!</Title>
        <Subtitle>
          {userData?.displayName ? `Hi ${userData.displayName}, ` : 'Hi there, '}
          here's your health journey overview
        </Subtitle>
      </Header>
      
      <StatsGrid>
        <StatCard>
          <StatContent>
            <StatIcon>
              <FaWeight />
            </StatIcon>
            <StatInfo>
              <StatValue>{healthData?.weight || '--'}</StatValue>
              <StatLabel>Current Weight (kg)</StatLabel>
            </StatInfo>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatIcon>
              <FaChartLine />
            </StatIcon>
            <StatInfo>
              <StatValue>{calculateBMI()}</StatValue>
              <StatLabel>Body Mass Index</StatLabel>
            </StatInfo>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatIcon>
              <FaUtensils />
            </StatIcon>
            <StatInfo>
              <StatValue>{mealPlans.length}</StatValue>
              <StatLabel>Active Meal Plans</StatLabel>
            </StatInfo>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatIcon>
              <FaCalendarAlt />
            </StatIcon>
            <StatInfo>
              <StatValue>{consultations.length}</StatValue>
              <StatLabel>Upcoming Sessions</StatLabel>
            </StatInfo>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Selected Plan Section */}
      <SectionContainer>
        <SectionHeader>
          <SectionTitle>
            <SectionIcon><FaClipboardList /></SectionIcon>
            Your Current Plan
          </SectionTitle>
        </SectionHeader>
        
        {selectedPlan ? (
          <PlanCard>
            <PlanHeader>
              <PlanTitleRow>
                <PlanTitle>
                  <FaClipboardList />
                  {selectedPlan.name || selectedPlan.title || 'Health Plan'}
                </PlanTitle>
                <PlanTitle>
                  $
                  {selectedPlan.price || selectedPlan.title || 'Health Plan'}
                </PlanTitle>
                <PlanStatus status={planStatus}>
  {userData?.isPlanPaused
    ? 'Paused by Coach'
    : planStatus === 'expired'
      ? 'Completed'
      : 'Active'}
</PlanStatus>

              </PlanTitleRow>
            </PlanHeader>

            <PlanContent>
              {planProgress && planStatus !== 'expired' && (
                <ProgressContainer>
                  <ProgressHeader>
                    <ProgressLabel>Progress</ProgressLabel>
                    <ProgressPercentage>{Math.round(planProgress.progressPercentage || 0)}%</ProgressPercentage>
                  </ProgressHeader>
                  <ProgressBar>
                    <ProgressFill progress={planProgress.progressPercentage || 0} />
                  </ProgressBar>
                  <ProgressInfo>
                    <span>Started {formatDate(userData?.planSelectedAt)}</span>
                    <span>{getDaysRemaining()} days remaining</span>
                  </ProgressInfo>
                </ProgressContainer>
              )}

              <PlanDetailsGrid>
                <PlanDetail>
                  <h4>Duration</h4>
                  <p>{selectedPlan.duration || 'N/A'} days</p>
                </PlanDetail>
                <PlanDetail>
                  <h4>Goal</h4>
                  <p>{selectedPlan.goal || selectedPlan.targetGoal || 'N/A'}</p>
                </PlanDetail>
                <PlanDetail>
                  <h4>Started On</h4>
                  <p>{formatDate(userData?.planSelectedAt)}</p>
                </PlanDetail>
                <PlanDetail>
                  <h4>End Date</h4>
                  <p>{getPlanEndDate()}</p>
                </PlanDetail>
                {userData?.planPausedDays > 0 && (
                  <PlanDetail>
                    <h4>Days Extended</h4>
                    <p>{userData.planPausedDays} {userData.planPausedDays === 1 ? 'day' : 'days'}</p>
                  </PlanDetail>
                )}
                {selectedPlan.targetCalories && (
                  <PlanDetail>
                    <h4>Target Calories</h4>
                    <p>{selectedPlan.targetCalories} cal/day</p>
                  </PlanDetail>
                )}
              </PlanDetailsGrid>

              <ActionButtons>
                {planStatus === 'expired' ? (
  <>
    <EnhancedButton onClick={rebuyPlan} variant="primary">
      <FaCheckCircle />
      Rebuy This Plan (${selectedPlan.price})
    </EnhancedButton>
    <EnhancedButton as={Link} to="/customer/select-plan" variant="outline">
      Select Different Plan
    </EnhancedButton>
    <EnhancedButton as={Link} to="/customer/plan-details" variant="outline">
      View Completed Plan
      <FaArrowRight />
    </EnhancedButton>
  </>
) : (
                  <EnhancedButton as={Link} to="/customer/plan-details" variant="primary">
                    View Full Plan
                    <FaArrowRight />
                  </EnhancedButton>
                )}
              </ActionButtons>
            </PlanContent>
          </PlanCard>
        ) : (
          <EmptyState>
            <h3>No Plan Selected</h3>
            <p>Start your health journey by selecting a personalized plan that fits your goals!</p>
            <EnhancedButton as={Link} to="/customer/select-plan" variant="primary">
              <FaCheckCircle />
              Choose Your Plan
            </EnhancedButton>
          </EmptyState>
        )}
      </SectionContainer>
      
      {partners.length > 0 && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon><FaUser /></SectionIcon>
              Your Healthcare Team
            </SectionTitle>
          </SectionHeader>
          <ContentCard>
            <ContentHeader>
              <h3 style={{ margin: 0, color: '#4a5568' }}>Healthcare Partners</h3>
            </ContentHeader>
            {partners.map(partner => (
              <ContentItem key={partner.id}>
                <ItemContent>
                  <ItemInfo>
                    <ItemTitle>{partner.displayName || 'Healthcare Partner'}</ItemTitle>
                    <ItemDescription>{partner.specialization || 'General Healthcare'}</ItemDescription>
                  </ItemInfo>
                  <EnhancedButton 
                    as={Link} 
                    to="/customer/chat" 
                    variant="outline" 
                    size="small"
                  >
                    Chat Now
                    <FaArrowRight />
                  </EnhancedButton>
                </ItemContent>
              </ContentItem>
            ))}
          </ContentCard>
        </SectionContainer>
      )}
      
      {consultations.length > 0 && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon><FaCalendarAlt /></SectionIcon>
              Upcoming Consultations
            </SectionTitle>
          </SectionHeader>
          <ContentCard>
            {consultations.slice(0, 3).map(consultation => (
              <ContentItem key={consultation.id}>
                <ItemContent>
                  <ItemInfo>
                    <ItemTitle>{consultation.title || 'Consultation'}</ItemTitle>
                    <ItemDescription>
                      {consultation.date?.toDate().toLocaleDateString() || 'No date'} at {consultation.time || 'No time'}
                    </ItemDescription>
                  </ItemInfo>
                  <EnhancedButton 
                    as={Link} 
                    to={`/customer/consultations/${consultation.id}`} 
                    variant="outline" 
                    size="small"
                  >
                    View Details
                    <FaArrowRight />
                  </EnhancedButton>
                </ItemContent>
              </ContentItem>
            ))}
            
            {consultations.length > 3 && (
              <ViewAllButton>
                <EnhancedButton as={Link} to="/customer/consultations" variant="outline">
                  View All Consultations
                  <FaArrowRight />
                </EnhancedButton>
              </ViewAllButton>
            )}
          </ContentCard>
        </SectionContainer>
      )}
      
      {mealPlans.length > 0 && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon><FaUtensils /></SectionIcon>
              Your Meal Plans
            </SectionTitle>
          </SectionHeader>
          <ContentCard>
            {mealPlans.slice(0, 3).map(plan => (
              <ContentItem key={plan.id}>
                <ItemContent>
                  <ItemInfo>
                    <ItemTitle>{plan.title || 'Meal Plan'}</ItemTitle>
                    <ItemDescription>{plan.description || 'Personalized nutrition plan'}</ItemDescription>
                  </ItemInfo>
                  <EnhancedButton 
                    as={Link} 
                    to={`/customer/meal-plans/${plan.id}`} 
                    variant="outline" 
                    size="small"
                  >
                    View Plan
                    <FaArrowRight />
                  </EnhancedButton>
                </ItemContent>
              </ContentItem>
            ))}
            
            {mealPlans.length > 3 && (
              <ViewAllButton>
                <EnhancedButton as={Link} to="/customer/meal-plans" variant="outline">
                  View All Meal Plans
                  <FaArrowRight />
                </EnhancedButton>
              </ViewAllButton>
            )}
          </ContentCard>
        </SectionContainer>
      )}
    </PageContainer>
  );
};

export default CustomerDashboard;